import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useRecoveryMode } from "@/lib/auth/recoveryMode";

/**
 * Task #5620 — routes a password-recovery callback to the set-password form.
 *
 * When Supabase consumes a recovery link (see lib/auth/recoveryMode.ts) the
 * app enters recovery mode. Regardless of where Supabase lands the browser
 * (the intended `/reset-password`, or the homepage on a Site-URL / redirect
 * mismatch), this redirector navigates to `/reset-password` so the user always
 * reaches the form instead of being silently logged in.
 *
 * It redirects at most once: after the initial hand-off it stands down, so the
 * "back to login" link and the post-reset navigation to `/dashboard` are never
 * yanked back to the form. `location` here is already locale-stripped by the
 * router base, so a bare `/reset-password` comparison is correct.
 */
export function RecoveryModeRedirector() {
  const recoveryMode = useRecoveryMode();
  const [location, navigate] = useLocation();
  const handledRef = useRef(false);
  useEffect(() => {
    if (!recoveryMode) return;
    if (handledRef.current) return;
    handledRef.current = true;
    if (location !== "/reset-password") {
      navigate("/reset-password", { replace: true });
    }
  }, [recoveryMode, location, navigate]);
  return null;
}
